// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Category Master helpers (Stage 6B.8A-3N).
//
// Pure JS — tested by the frontend node:test runner.
// ─────────────────────────────────────────────────────────────────

// Mirrors backend migration 0053 seeds AND the prior frontend
// hardcoded GLASS_CATEGORY_OPTIONS. Used as a fallback only — the
// runtime source of truth is the DB master.
export const DEFAULT_GLASS_CATEGORIES = Object.freeze([
  { sno: 1, categoryName: 'CLEAR',       glassFamily: 'clear',      glassColor: 'CLEAR'       },
  { sno: 2, categoryName: 'TINTED',      glassFamily: 'tinted',     glassColor: 'TINTED'      },
  { sno: 3, categoryName: 'REFLECTIVE',  glassFamily: 'reflective', glassColor: 'REFLECTIVE'  },
  { sno: 4, categoryName: 'EXTRA CLEAR', glassFamily: 'clear',      glassColor: 'EXTRA CLEAR' },
  { sno: 5, categoryName: 'MIRROR',      glassFamily: 'mirror',     glassColor: 'MIRROR'      },
  { sno: 6, categoryName: 'LACQUERED',   glassFamily: 'coated',     glassColor: 'LACQUERED'   },
  { sno: 7, categoryName: 'FLUTED',      glassFamily: 'frosted',    glassColor: 'FLUTED'      },
  { sno: 8, categoryName: 'SPECIAL',     glassFamily: 'other',      glassColor: 'SPECIAL'     },
  { sno: 9, categoryName: 'OTHER',       glassFamily: 'other',      glassColor: 'OTHER'       }
]);

export const GLASS_FAMILY_OPTIONS = Object.freeze([
  { value: 'clear',      label: 'Clear'      },
  { value: 'tinted',     label: 'Tinted'     },
  { value: 'reflective', label: 'Reflective' },
  { value: 'coated',     label: 'Coated'     },
  { value: 'mirror',     label: 'Mirror'     },
  { value: 'frosted',    label: 'Frosted'    },
  { value: 'laminated',  label: 'Laminated'  },
  { value: 'insulated',  label: 'Insulated'  },
  { value: 'other',      label: 'Other'      }
]);

const GLASS_FAMILY_VALUES = new Set(GLASS_FAMILY_OPTIONS.map((o) => o.value));

// ── normalizeGlassCategoryRows ─────────────────────────────────
// Coerces API row shape into the editor's working shape and assigns
// a stable _clientRowId so React keys never collide while editing a
// brand-new (server-id-less) row.
let _rowIdCounter = 0;
function nextRowId() { _rowIdCounter += 1; return `gc-c-${_rowIdCounter}`; }

export function normalizeGlassCategoryRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    _clientRowId: r._clientRowId || (r.id ? `gc-${r.id}` : nextRowId()),
    _dirty:        false,
    id:            r.id || null,
    sno:           r.sno != null ? Number(r.sno) : null,
    categoryName:  r.categoryName || '',
    glassFamily:   r.glassFamily  || '',
    glassColor:    r.glassColor   || '',
    isActive:      r.isActive !== false,
    remarks:       r.remarks || ''
  }));
}

export function markDirtyRow(rows, clientRowId, patch) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => r._clientRowId === clientRowId
    ? { ...r, ...patch, _dirty: true }
    : r);
}

// ── nextSno ─────────────────────────────────────────────────────
// Suggest the next sno for a brand-new row: max(existing) + 1, or 1
// when the grid is empty.
export function nextSno(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 1;
  let max = 0;
  for (const r of rows) {
    const n = Number(r && r.sno);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

// ── sortGlassCategories ─────────────────────────────────────────
export function sortGlassCategories(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const as = a && a.sno != null ? Number(a.sno) : Number.POSITIVE_INFINITY;
    const bs = b && b.sno != null ? Number(b.sno) : Number.POSITIVE_INFINITY;
    if (as !== bs) return as - bs;
    return String((a && a.categoryName) || '')
      .localeCompare(String((b && b.categoryName) || ''));
  });
}

// ── Payload builders ────────────────────────────────────────────
export function buildGlassCategoryPayload(row) {
  if (!row || typeof row !== 'object') return {};
  const out = {
    categoryName: trimOrNull(row.categoryName),
    glassFamily:  trimOrNull(row.glassFamily),
    glassColor:   trimOrNull(row.glassColor),
    isActive:     row.isActive !== false,
    remarks:      trimOrNull(row.remarks)
  };
  if (row.sno != null && row.sno !== '') out.sno = Math.trunc(Number(row.sno));
  return out;
}

// `rows` is the entire editor working set; emits `{ rows, deletes }`
// shaped for the backend bulk-save endpoint. Only dirty rows + queued
// deletes go on the wire — clean rows are skipped to keep the
// payload tight and the audit row meaningful.
export function buildGlassCategoryBulkPayload(rows, deletedIds = []) {
  const dirty = (rows || []).filter((r) => r && r._dirty);
  return {
    rows: dirty.map((r) => {
      const out = buildGlassCategoryPayload(r);
      if (r.id) out.id = r.id;
      if (r._clientRowId) out._clientRowId = r._clientRowId;
      return out;
    }),
    deletes: Array.isArray(deletedIds)
      ? deletedIds.filter((id) => typeof id === 'string' && id.length > 0)
      : []
  };
}

// ── Validation ──────────────────────────────────────────────────
// Lightweight last-mile checks before opening the network call.
// Returns { ok, errors: [{ field, message, clientRowId? }] }.
export function validateGlassCategoryRow(row, allRows = []) {
  const errors = [];
  if (!row || typeof row !== 'object') {
    return { ok: false, errors: [
      { field: 'row', message: 'Row is required.' }
    ] };
  }
  const name  = String(row.categoryName || '').trim();
  const fam   = String(row.glassFamily || '').trim();
  const color = String(row.glassColor || '').trim();
  if (!name) {
    errors.push({ field: 'categoryName',
      message: 'Category Name is required.',
      clientRowId: row._clientRowId });
  }
  if (!fam) {
    errors.push({ field: 'glassFamily',
      message: 'Glass Family is required.',
      clientRowId: row._clientRowId });
  } else if (!GLASS_FAMILY_VALUES.has(fam)) {
    errors.push({ field: 'glassFamily',
      message: `Glass Family must be one of: ${
        [...GLASS_FAMILY_VALUES].join(', ')}.`,
      clientRowId: row._clientRowId });
  }
  if (!color) {
    errors.push({ field: 'glassColor',
      message: 'Glass Color is required.',
      clientRowId: row._clientRowId });
  }
  if (row.sno != null && row.sno !== '') {
    const n = Number(row.sno);
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      errors.push({ field: 'sno',
        message: 'S No must be greater than zero.',
        clientRowId: row._clientRowId });
    }
  }
  // Cross-row duplicate checks (case-insensitive name + sno).
  if (Array.isArray(allRows) && allRows.length > 0) {
    const peers = allRows.filter((r) =>
      r && r._clientRowId !== row._clientRowId);
    const sno = Number(row.sno);
    if (Number.isFinite(sno) && sno > 0) {
      for (const p of peers) {
        if (Number(p.sno) === sno) {
          errors.push({ field: 'sno',
            message: `S No ${sno} is already used by another row.`,
            clientRowId: row._clientRowId });
          break;
        }
      }
    }
    if (name) {
      const lower = name.toLowerCase();
      for (const p of peers) {
        if (String(p.categoryName || '').trim().toLowerCase() === lower) {
          errors.push({ field: 'categoryName',
            message: `Category Name "${name}" already exists.`,
            clientRowId: row._clientRowId });
          break;
        }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateGlassCategoryRows(rows) {
  const all = Array.isArray(rows) ? rows : [];
  const errors = [];
  for (const r of all) {
    const { ok, errors: rowErrors } = validateGlassCategoryRow(r, all);
    if (!ok) errors.push(...rowErrors);
  }
  return { ok: errors.length === 0, errors };
}

// ── Private helpers ─────────────────────────────────────────────
function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}
